import { useEffect, useState } from "react";
import { Space, Button, Row, Col, Input, Upload, Select, Form, notification, Modal, Checkbox, Switch } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import ErrorDetails from "../../components/ErrorDetails";
import {
  useGetBrandQuery,
} from "../../redux/services/brandApi";
import { BASE_URL } from "../../redux/services/baseApi";
import { useAppSelector } from "../../redux/hooks";
import EditorComponent from "../../components/EditorComponent";

const { Option } = Select;

const BrandForm = (props: any) => {
  const { isOpen, onCancel, brandId, refetch } = props;

  const [inProgress, setInProgress] = useState(false);
  const [editorValue, setEditorValue] = useState("");

  const [api, contextHolder] = notification.useNotification();
  const auth = useAppSelector<any>((store) => store.auth);
  const user = auth.user;

  const [form] = Form.useForm();

  const { data: brandData, isLoading } = useGetBrandQuery(brandId);
  const selectedBrandData: any = brandData?.brand;

  const isSuperUser = user?.roles?.some((role: { role_type: string }) =>
    ["super_user"].includes(role.role_type)
  );

  useEffect(() => {
    if (selectedBrandData) {
      const fieldsValue = { ...selectedBrandData };
      form.setFieldsValue(fieldsValue);
    }
    return () => {
      form.resetFields();
    };
  }, [selectedBrandData, brandId]);

  const onFinish = (values: any) => {
    if (brandId) {
      onUpdateBrand(values);
    } else {
      addBrand(values);
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };

  const normFile = (e: any) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  const addBrand = async (values: any) => {
    try {
      setInProgress(true);
      const brand = new FormData();

      const brandFields = [
        "name",
        "code",
        "contact_person",
        "contact_number",
        "alt_contact_number",
        "status",
        "gst",
        "billing_address",
        "terms_and_conditions",
      ];

      brandFields.forEach((field) => {
        if (values?.[field]) {
          brand.append(`brand[${field}]`, values[field]);
        }
      });

      brand.append("brand[add_to_forms]", values["add_to_forms"]);
      brand.append("brand[next_follow_up_required]", values["next_follow_up_required"]);

      const brandLogo = values?.brand_logo;

      if (brandLogo && Array.isArray(brandLogo) && brandLogo.length > 0) {
        brand.append("brand[brand_logo]", brandLogo[0].originFileObj);
      }
      const response = await fetch(`${BASE_URL}brands`, {
        method: "POST",
        headers: {
          "access-token": auth.accessToken,
          "token-type": "Bearer",
          client: auth.client,
          uid: auth.uid
        },
        body: brand
      });
      const responseData = await response.json();

      if (responseData?.status === "Successful") {
        api.success({
          message: "Create brand successful",
          placement: "top"
        });
        form.resetFields();
        onCancel();
        refetch();
        setInProgress(false);
      } else {
        api.info({
          message: "Create brand failed",
          description: <ErrorDetails errors={responseData || []} />,
          placement: "top"
        });
        setInProgress(false);
      }
    } catch (error: any) {
      api.info({
        message: "Create brand failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setInProgress(false);
    }
  };

  const onUpdateBrand = async (values: any) => {

    try {
      setInProgress(true);
      const brand = new FormData();

      const brandFields = [
        "name",
        "code",
        "contact_person",
        "contact_number",
        "alt_contact_number",
        "status",
        "gst",
        "billing_address",
        "terms_and_conditions",
      ];

      brandFields.forEach((field) => {
        if (values?.[field]) {
          brand.append(`brand[${field}]`, values[field]);
        }
      });

      brand.append("brand[next_follow_up_required]", values["next_follow_up_required"]);
      brand.append("brand[outstation_distance_required]", values["outstation_distance_required"]);
      const brandLogo = values?.brand_logo;

      if (brandLogo && Array.isArray(brandLogo) && brandLogo.length > 0) {
        brand.append("brand[brand_logo]", brandLogo[0].originFileObj);
      }

      const response = await fetch(`${BASE_URL}brands/${brandId}`, {
        method: "PUT",
        headers: {
          "access-token": auth.accessToken,
          "token-type": "Bearer",
          client: auth.client,
          uid: auth.uid
        },
        body: brand
      });

      const responseData = await response.json();

      if (responseData?.status === "Successful") {
        api.success({
          message: "Update brand successful",
          placement: "top"
        });
        form.resetFields();
        onCancel();
        setInProgress(false);
        refetch();
      } else {
        api.info({
          message: "Update brand failed",
          description: <ErrorDetails errors={responseData || []} />,
          placement: "top"
        });
        setInProgress(false);
      }
    } catch (error: any) {
      api.info({
        message: "Update brand failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setInProgress(false);
    }
  };

  return (
    <Modal
      title={`${brandId ? "Update" : "Create"} Brand`}
      visible={isOpen}
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      footer={false}
      width={1000}
      style={{ top: 0 }}
      bodyStyle={{ paddingTop: 0 }}
    >
      {contextHolder}
      <Form
        name="brand_form"
        form={form}
        initialValues={{ remember: true, add_to_forms: true }}
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        autoComplete="off"
        disabled={inProgress || isLoading}
      >
        <Form.Item
          label="Name"
          name="name"
          rules={[
            { required: true, message: "Please input name" },
            { min: 3, message: "Name must be minimum 3 characters." },
            { max: 30, message: "Name must be maximum 30 characters." }
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Code"
          name="code"
          rules={[
            { min: 2, message: "Code must be minimum 2 characters." },
            { max: 5, message: "Code must be maximum 5 characters." }
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Contact Person Name"
          name="contact_person"
          rules={[
            { min: 3, message: "Contact Person Name must be minimum 3 characters." },
            { max: 30, message: "Contact Person Name must be maximum 30 characters." }
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Contact Number"
          name="contact_number"
          rules={[
            { min: 10, message: "Contact Number must be minimum 10 digit." },
            { max: 10, message: "Contact Number be maximum 10 digit." },
            { pattern: /^[0-9]+$/, message: "Mobile No. must contain only numbers." }
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Another Contact Number"
          name="alt_contact_number"
          rules={[
            { min: 10, message: "Another Contact Number must be minimum 10 digit." },
            { max: 10, message: "Another Contact Number be maximum 10 digit." },
            { pattern: /^[0-9]+$/, message: "Contact Number must contain only numbers." }
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="status"
          label="Status"
          rules={[{ required: true, message: "Please select status" }]}
        >
          <Select
            placeholder="Select status"
            showSearch
            optionFilterProp="children"
            filterOption={(input, option: any) =>
              option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            <Option value="active">Active</Option>
            <Option value="inactive">Inactive</Option>
            <Option value="disabled">Disabled</Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="GST Number"
          name="gst"
          rules={[
            { min: 15, message: "GST Number must be minimum 15 digit." },
            { max: 15, message: "GST Number be maximum 15 digit." }
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Term and Conditions"
          name="terms_and_conditions"
        >
          <EditorComponent value={editorValue} onChange={setEditorValue} />
        </Form.Item>
        {!brandId && (
          <Form.Item label="Add to forms" name="add_to_forms" valuePropName="checked">
            <Checkbox />
          </Form.Item>
        )}
        <Form.Item
          label="Billing Address"
          name="billing_address"
          rules={[
            { min: 3, message: "Billing Address must be minimum 3 digit." },
            { max: 255, message: "Billing Address be maximum 255 digit." }
          ]}
        >
          <Input />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Upload Logo"
              name="brand_logo"
              valuePropName="brand_logo"
              getValueFromEvent={normFile}
            >
              <Upload
                accept="image/png, image/jpeg"
                maxCount={1}
                listType="picture-card"
                beforeUpload={() => {
                  return false;
                }}
                multiple={false}
                defaultFileList={
                  selectedBrandData?.brand_logo
                    ? [
                      {
                        uid: "-1",
                        name: "image.jpg",
                        status: "done",
                        url: selectedBrandData?.brand_logo
                      }
                    ]
                    : []
                }
              >
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>Upload</div>
                </div>
              </Upload>
            </Form.Item>
          </Col>

          {isSuperUser && <Col xs={24} md={12}>
            <Form.Item
              label="Is Next Follow Up Required ?"
              name="next_follow_up_required"
              valuePropName="checked"
            >
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>
            <Form.Item
              label="Is Outstation Distance Required ?"
              name="outstation_distance_required"
              valuePropName="checked"
            >
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>
          </Col>}
        </Row>

        <Form.Item wrapperCol={{ offset: 0, span: 24 }}>
          <Row>
            <Col>
              <Space size={"middle"}>
                <Button
                  type="default"
                  onClick={() => {
                    form.resetFields();
                    onCancel();
                  }}
                >
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit" loading={inProgress}>
                  {brandId ? "Update" : "Add"}
                </Button>
              </Space>
            </Col>
          </Row>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default BrandForm;
